import React, { useState, useEffect } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { Button, LinkList } from '../../../components'
import { useMessage, useModalDialog } from '../../../hooks'
import { clientService, clientCaseService } from '../services'
import { companyService } from '../../../services'
import { useClientCaseDialog } from '../hooks'
import { ReviewTrendSection } from '../components/ReviewTrendSection'
import { ClientContacts } from '../components/ClientContacts'
import { RecruitmentCandidates } from '../components/RecruitmentCandidates'
import { appName } from '../appConfig'
import '../index.css'

const TABS = [
  { key: 'cases',    label: 'Case' },
  { key: 'reviews',  label: 'Utvärderingar' },
  { key: 'contacts', label: 'Kontakter' },
]

// Deep-linkable via three routes (/:slug, /:slug/reviews, /:slug/contacts) —
// the initial tab is derived from which route matched, not local state.
const tabFromPath = (pathname) => {
  if (pathname.endsWith('/reviews')) return 'reviews'
  if (pathname.endsWith('/contacts')) return 'contacts'
  return 'cases'
}

const ClientDetail = () => {
  const { slug } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { showMessage } = useMessage()
  const { hideModalDialog } = useModalDialog()
  const { showClientDialog, showDeleteClientDialog } = useClientCaseDialog()

  const [client, setClient]   = useState(null)
  const [company, setCompany] = useState(null)
  const [cases, setCases]     = useState([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState(tabFromPath(location.pathname))

  useEffect(() => { setActiveTab(tabFromPath(location.pathname)) }, [location.pathname])

  const fetchClient = () => {
    setLoading(true)
    clientService.getBySlug(slug)
      .then(res => {
        setClient(res.client)
        return Promise.all([
          companyService.getByUuid(res.client.client_company_uuid),
          clientCaseService.getByClient(slug),
        ])
      })
      .then(([companyRes, casesRes]) => {
        setCompany(companyRes.company)
        setCases(casesRes.clientCases || [])
        setLoading(false)
      })
      .catch(() => { showMessage('Kunde inte ladda kund', 'error'); setLoading(false) })
  }

  useEffect(() => { fetchClient() }, [slug]) // eslint-disable-line react-hooks/exhaustive-deps

  const changeTab = (tab) => {
    setActiveTab(tab)
    if (tab === 'cases') navigate(`/${appName}/${slug}`)
    else navigate(`/${appName}/${slug}/${tab}`)
  }

  const editClient = () => {
    showClientDialog(client, () => { showMessage('Sparad!'); fetchClient() })
  }

  const deleteClient = () => {
    showDeleteClientDialog({ ...client, case_count: cases.length }, async () => {
      await clientService.remove(client.client_uuid)
        .then(() => { hideModalDialog(); showMessage('Borttagen'); navigate(`/${appName}/list`) })
        .catch(err => showMessage(err.message || 'Kunde inte ta bort', 'error'))
    })
  }

  if (loading || !client) return <div className="cc-page"><p style={{ color: 'var(--color-dark-grey)' }}>Laddar…</p></div>

  return (
    <div className="cc-page">
      <div className="cc-page-header">
        <h1>{company?.company_name}</h1>
        <div style={{ display: 'flex', gap: 10 }}>
          <Button value="Redigera" className="button" onClick={editClient} />
          <Button value="Ta bort" className="button cc-danger-button" onClick={deleteClient} />
        </div>
      </div>

      <div className="cc-form-wrap">
        <div className="FormTabs">
          <div className="FormTabsHeader" style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--color-light-grey)' }}>
            {TABS.map(t => (
              <button
                key={t.key}
                className={`FormTab-button${activeTab === t.key ? ' active' : ''}`}
                onClick={() => changeTab(t.key)}
                style={{
                  padding: '14px 20px', border: 'none', background: 'none', cursor: 'pointer',
                  fontFamily: activeTab === t.key ? 'Rubik-Medium' : 'Rubik-Regular',
                  borderBottom: activeTab === t.key ? '2px solid var(--color-black)' : '2px solid transparent',
                }}
              >{t.label}</button>
            ))}
          </div>

          <div className="cc-section">
            {activeTab === 'cases' && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                  <div className="cc-meta-row">
                    <div className="cc-meta-item">
                      <div className="cc-meta-label">Bransch</div>
                      <div className="cc-meta-value">{company?.company_industry || '—'}</div>
                    </div>
                    <div className="cc-meta-item">
                      <div className="cc-meta-label">Status</div>
                      <span className={`pill ${client.client_status === 'active' ? 'pill-green' : 'pill-grey'}`}>
                        {client.client_status === 'active' ? 'Aktiv' : client.client_status === 'inactive' ? 'Inaktiv' : client.client_status}
                      </span>
                    </div>
                  </div>
                  <Button value="+ Nytt case" className="button main-button" onClick={() => navigate(`/${appName}/cases/new?client=${slug}`)} />
                </div>
                {cases.length === 0
                  ? <p style={{ color: 'var(--color-dark-grey)', fontStyle: 'italic' }}>Inga case ännu.</p>
                  : (
                    <table className="cc-table">
                      <thead><tr><th></th><th>Case</th><th>Status</th><th>Uppdaterad</th></tr></thead>
                      <tbody>
                        {cases.map(c => (
                          <tr key={c.client_case_uuid}>
                            <td><button className="button" onClick={() => navigate(`/${appName}/cases/${c.client_case_slug}${c.has_html ? '' : '/edit'}`)}>Visa</button></td>
                            <td>{c.client_case_slug}</td>
                            <td>{c.has_html ? <span className="pill pill-green">Publicerad</span> : <span className="pill pill-grey">Ej genererad</span>}</td>
                            <td style={{ color: 'var(--color-dark-grey)' }}>{c.modified_timestamp || c.created_timestamp}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )
                }
              </div>
            )}

            {activeTab === 'reviews' && <ReviewTrendSection clientSlug={slug} />}

            {activeTab === 'contacts' && (
              <div>
                <ClientContacts clientSlug={slug} />
                <div className="cc-section-title" style={{ marginTop: 32 }}>Rekryteringskandidater</div>
                <RecruitmentCandidates clientSlug={slug} />
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="cc-form-wrap" style={{ marginTop: 24 }}>
        <div className="cc-section">
          <LinkList
            title="Länkar"
            titleClassName="cc-section-title"
            links={company?.links || []}
            onAdd={(link) => companyService.addLink(client.client_company_uuid, { ...link, source: 'clients' }).then(res => { fetchClient(); return res.link })}
            onRemove={(link) => companyService.removeLink(client.client_company_uuid, link.company_link_id).then(fetchClient)}
            sourceLabels={{ clients: 'Kunder' }}
          />
        </div>
      </div>
    </div>
  )
}

export default ClientDetail
